/**
 * 用途の結果の表示データ（ChatResultBlock）を画面で整えて見せる。
 *
 * 数字や並びはサーバーが計算したもので、モデルの文章ではない。
 * 日程の各行の［この日に確定］は、サーバーが変更案を組む（確定は確認カードで行う）。
 */

import { Link } from 'react-router-dom';
import { CHAT_ENTRY_ACTION, CHAT_RESULT_KIND } from '@/constants/chat';
import type { ChatResultBlock } from '@/services/chat';

interface ChatResultBlocksProps {
  blocks: ChatResultBlock[];
  busy: boolean;
  /** 日程の行の［この日に確定］など。無ければボタンを出さない */
  onAction?: (kind: string, target: string) => void;
  onNavigate: () => void;
}

function Links({ block, onNavigate }: { block: ChatResultBlock; onNavigate: () => void }) {
  if (!block.links?.length) return null;
  return (
    <ul className="space-y-1">
      {block.links.map((link) => (
        <li key={link.path}>
          <Link to={link.path} onClick={onNavigate} className="text-xs text-primary-700 underline underline-offset-2">
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Empty({ block }: { block: ChatResultBlock }) {
  return <p className="text-xs text-text-500">{block.emptyText ?? 'ありません'}</p>;
}

function Block({ block, busy, onAction, onNavigate }: { block: ChatResultBlock } & Omit<ChatResultBlocksProps, 'blocks'>) {
  switch (block.kind) {
    case CHAT_RESULT_KIND.DATE_RANKING:
      return (
        <ol className="space-y-1.5">
          {(block.dateRows ?? []).map((row) => (
            <li
              key={row.optionId}
              className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg px-2.5 py-2 text-xs tabular-nums ${
                row.top ? 'bg-primary-50' : 'bg-text-50'
              }`}
            >
              <span className="font-medium text-text-900">{row.label}</span>
              <span className="text-text-600">
                参加 {row.yes}・保留 {row.maybe}・不可 {row.no}
              </span>
              {row.unavailable && row.unavailable.length > 0 && (
                <span className="w-full text-[11px] text-text-500">不可: {row.unavailable.join('、')}</span>
              )}
              {onAction && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onAction(CHAT_ENTRY_ACTION.PROPOSE_DATE, row.optionId)}
                  className="ml-auto rounded-lg border border-primary-400 px-2 py-1 text-[11px] text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                >
                  この日に確定
                </button>
              )}
            </li>
          ))}
        </ol>
      );
    case CHAT_RESULT_KIND.SCHEDULE:
      if (!block.scheduleRows?.length) return <Empty block={block} />;
      return (
        <ul className="space-y-1.5">
          {block.scheduleRows.map((row) => (
            <li key={row.eventId}>
              <Link
                to={row.link}
                onClick={onNavigate}
                className="flex flex-col rounded-lg bg-text-50 px-2.5 py-2 hover:bg-primary-50"
              >
                <span className="text-sm text-text-900">{row.title}</span>
                <span className="text-[11px] text-text-500">
                  {[
                    row.dateLabel,
                    row.roleLabel,
                    row.myStatus,
                    row.pendingTodos ? `やることの残り ${row.pendingTodos} 件` : null,
                  ]
                    .filter(Boolean)
                    .join('・')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      );
    case CHAT_RESULT_KIND.TASKS:
      if (!block.taskRows?.length) return <Empty block={block} />;
      return (
        <ul className="space-y-1">
          {block.taskRows.map((row, index) => (
            <li key={`${row.eventId}-${index}`} className="text-xs text-text-700">
              <span className="text-text-500">{row.eventTitle}: </span>
              {row.content}
              {row.dueDate && <span className="text-text-500">（{row.dueDate}まで）</span>}
            </li>
          ))}
        </ul>
      );
    case CHAT_RESULT_KIND.BUDGET:
      if (!block.budget) return null;
      return (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-text-50 px-3 py-2 text-xs tabular-nums">
          <dt className="text-text-500">予算</dt>
          <dd className="text-text-900">{block.budget.amount.toLocaleString()} 円</dd>
          <dt className="text-text-500">参加 {block.budget.accepted} 人で割ると</dt>
          <dd className="font-medium text-text-900">1 人 {block.budget.perAccepted.toLocaleString()} 円</dd>
          {block.budget.perWithMaybe != null && (
            <>
              <dt className="text-text-500">保留の {block.budget.maybe} 人も来たら</dt>
              <dd className="text-text-900">1 人 {block.budget.perWithMaybe.toLocaleString()} 円</dd>
            </>
          )}
        </dl>
      );
    case CHAT_RESULT_KIND.SECTIONS:
      return (
        <div className="space-y-2">
          {(block.sections ?? []).map((section) => (
            <div key={section.label}>
              <p className="text-[11px] font-medium text-text-500">{section.label}</p>
              <ul className="list-disc pl-4 text-xs text-text-800">
                {(section.items ?? []).map((item, index) => (
                  <li key={index} className="break-words">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function ChatResultBlocks({ blocks, busy, onAction, onNavigate }: ChatResultBlocksProps) {
  if (blocks.length === 0) return null;
  return (
    <div className="mt-2 space-y-3">
      {blocks.map((block, index) => (
        <div key={index} className="space-y-1.5">
          {block.title && <p className="text-xs font-semibold text-text-700">{block.title}</p>}
          <Block block={block} busy={busy} onAction={onAction} onNavigate={onNavigate} />
          <Links block={block} onNavigate={onNavigate} />
        </div>
      ))}
    </div>
  );
}
